import { Component, EventEmitter, Input, Output } from '@angular/core';
import { QuestionDto } from 'app/@types/dto/question-dto.model';
import { QuestionInstanceInputEntry, inputUpdateMessage } from '../question.component';
import { EnumEntryDto } from 'app/@types/dto/enum-entry-dto.model';
import { CustomTranslateService } from '@services/common/custom-translate.service';

@Component({
    selector: 'app-question-instance',
    templateUrl: './question-instance.component.html',
    styleUrls: ['./question-instance.component.scss'],
    standalone: false,
})
export class QuestionInstanceComponent {
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() question: QuestionDto;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() questionInstancesInput: QuestionInstanceInputEntry;
    @Input() domain1Map?: Map<string, EnumEntryDto>;
    @Input() domain1: EnumEntryDto[] = [];
    @Input() domainPerCode?: { [key: string]: EnumEntryDto[] };
    @Input() locale?: string;
    @Input() index: number = -1;
    @Input() id?: string;
    @Input() orgId?: string;
    @Input() currentEntityFte: number = 0;
    @Input() disabled: boolean = false;
    @Input() isLocked: boolean = false;
    // Read-only (Viewer role): mirrors the backend write block by disabling/hiding edit controls.
    @Input() readOnly: boolean = false;
    @Output() enumChange = new EventEmitter<QuestionInstanceInputEntry>();
    @Output() deleteEntry = new EventEmitter<QuestionInstanceInputEntry>();
    @Output() inputEscape = new EventEmitter<inputUpdateMessage>();
    @Output() focusNextInputEvent = new EventEmitter<string>();
    public maxValue1: number | undefined = undefined;
    public maxValue2: number | undefined = undefined;
    /** A value out of range: the message, shown under the field being typed in. */
    public rangeError?: { field: number; key: string };

    constructor(public translateService: CustomTranslateService) {}

    checkError(event: Event, inputField: number) {
        const target = event.target as HTMLInputElement;
        this.rangeError = undefined;
        if (this.question.questionCode === 'q015') {
            if (+(target.ariaValueNow ?? 0) > 52) {
                this.rangeError = { field: inputField, key: 'question.q015.error' };
            }
        } else if (this.question.questionCode === 'q016') {
            if (+(target.ariaValueNow ?? 0) > 5) {
                this.rangeError = { field: inputField, key: 'question.q016.error' };
            }
        } else if (this.question.option?.isFTEMax) {
            if (+(target.ariaValueNow ?? 0) > this.currentEntityFte) {
                this.maxValue1 = this.currentEntityFte;
                this.rangeError = { field: inputField, key: 'question.q020a.error' };
            }
        } else if (this.question.questionCode === 'q020a') {
            this.maxValue1 = 100;
            this.maxValue2 = 100;
            const val1 = this.questionInstancesInput.value1Control.value as string | null;
            const val2 = this.questionInstancesInput.value2Control.value as string | null;
            const control1 = val1 ? parseInt(val1, 10) : 0;
            const control2 = val2 ? parseInt(val2, 10) : 0;
            if (control1 + control2 > 100) {
                this.rangeError = { field: inputField, key: 'question.q020a.error' };
            }
            this.questionInstancesInput.value3Control.setValue(
                100 - control1 - control2 > 0 ? 100 - control1 - control2 : 0,
            );
            if (control1 > 99) {
                this.questionInstancesInput.value2Control.setValue(0);
                this.maxValue2 = 0;
            } else if (control2 > 99) {
                this.questionInstancesInput.value1Control.setValue(0);
                this.maxValue1 = 0;
            } else {
                if (inputField === 1) {
                    this.maxValue1 = 100 - control2;
                    this.maxValue2 = 100 - control1;
                } else {
                    this.maxValue2 = 100 - control1;
                    this.maxValue1 = 100 - control2;
                }
            }
        }
    }

    public notifyEscapeToParent(inputField: number) {
        // Viewer (read-only): leaving an input must not trigger a save — the backend
        // rejects the write (403). The field is inert anyway, so there is nothing to save.
        if (this.readOnly) {
            return;
        }
        this.inputEscape.emit({ inputEntry: this.questionInstancesInput, questionField: inputField });
    }

    public onEnumChangeToParent() {
        if (this.readOnly) {
            return;
        }
        this.enumChange.emit(this.questionInstancesInput);
    }

    public onDeleteNotif() {
        if (this.readOnly) {
            return;
        }
        this.deleteEntry.emit(this.questionInstancesInput);
    }

    /**
     * The accessible name of field 1, 2 or 3: the title shown above it, else the question's own
     * title for that field. The visible titles are only shown on the first row.
     */
    fieldLabel(field: 1 | 2 | 3): string | undefined {
        const base = `question.${this.question.questionCode}`;
        const key = [`${base}.entryTitle${field}`, `${base}.title${field}`].find((k) =>
            this.translateService.keyExists(k),
        );
        return key ? this.translateService.instant(key) : undefined;
    }

    focusNextInput(event: Event, id: string | undefined) {
        if ((event as KeyboardEvent).key === 'Enter' && id !== '') {
            event.preventDefault();
            this.focusNextInputEvent.emit(id);
        }
    }
}
